import type { Chain } from "viem/chains";
import {
  type ChainId,
  type VaultConfigResponse,
  fetchVaultConfigs,
} from "../api/vault-config";

// Cache to store PublicClient instances for different chains
let chainsCache: Map<ChainId, Chain> | null = null;

/**
 * Gets a chain object from the vault config response
 * @param chainId The chain ID to look up
 * @param config Optional config object. If not provided, will fetch from API
 * @returns Promise<Chain>
 */
export async function getChainFromConfig(
  chainId: ChainId,
  config?: VaultConfigResponse
): Promise<Chain> {
  // If we have a cache and no specific config was provided, use the cache
  if (chainsCache && !config) {
    const chain = chainsCache.get(chainId);
    if (chain) return chain;
  }

  // If no config provided, fetch it
  const vaultConfig = config ?? (await fetchVaultConfigs());

  // Get the chain from config
  const chain = vaultConfig.chains[chainId];
  if (!chain) {
    throw new Error(`Chain not found for ID: ${chainId}`);
  }

  // If no specific config was provided, update the cache
  if (config && !chainsCache) {
    chainsCache = new Map(
      Object.entries(config.chains).map(([id, chain]) => [
        Number(id) as ChainId,
        chain,
      ])
    );
  }

  return chain;
}

/**
 * Clears the chains cache
 */
export function clearChainsCache(): void {
  chainsCache = null;
}
